/**
 * SendStatementsFlow
 *
 * Self-contained component that manages the full Send Statements flow:
 *   Step 1 — SendStatementsOptionsModal  (statement date, currency, message, template)
 *   Steps 2-4 — CustomerSendEmailModal   (compose, recipients, SendIt MFE)
 *
 * Extracted from customers.tsx to keep that page lean.
 */

import { useState, useMemo, useCallback } from "react";
import { useCompany } from "@/contexts/CompanyContext";
import { toast } from "sonner";
import {
  SendStatementsOptionsModal,
  type SendStatementOptions,
} from "@/components/customers/send-statements-options-modal";
import { CustomerSendEmailModal } from "@/components/customers/customer-send-email-modal";
import { useStatementPdfAttachment } from "@/hooks/useStatementPdfAttachment";
import type { CustomerPreviewInput } from "@/lib/react-pdf/statement-data";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface StatementCustomer {
  id: string;
  name: string;
  accountNumber: string;
  currency: string;
  outstanding: number;
  overdue: number;
  creditLimit: number;
  currencyCreditLimit?: number;
  address: string;
  primaryAddressLine?: string;
  primaryAddressTown?: string;
  primaryAddressCounty?: string;
  primaryAddressPostCode?: string;
  primaryAddressCountry?: string;
  paymentTerms: string;
  lastPayment?: string;
  contact: string;
  phone?: string;
  primaryContactFirstName?: string;
  primaryContactSurname?: string;
  creditControllerName?: string;
  creditControllerEmail?: string;
  isAnyCurrency?: boolean;
  displayOutstanding?: number;
  displayOverdue?: number;
  outstandingHome?: number;
  overdueHome?: number;
}

export interface SendStatementsFlowProps {
  /** Controls visibility of Step 1 (options modal). Pass the same state used to open the flow. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** IDs of selected customers */
  selectedCustomerIds: string[];
  /** Full customer list — used to look up details for selected IDs */
  customers: StatementCustomer[];
  /** Called after emails are sent successfully */
  onSuccess: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function SendStatementsFlow({
  open,
  onOpenChange,
  selectedCustomerIds,
  customers,
  onSuccess,
}: Readonly<SendStatementsFlowProps>) {
  const { selectedCompany } = useCompany();
  const baseCurrency = selectedCompany?.currency ?? "GBP";
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [statementOptions, setStatementOptions] = useState<SendStatementOptions | null>(null);

  // Lookup map of customer preview data for PDF generation
  const customerDataById = useMemo(() => {
    const map = new Map<string, CustomerPreviewInput>();
    for (const id of selectedCustomerIds) {
      const c = customers.find((x) => x.id === id);
      if (c) {
        map.set(id, {
          name: c.name,
          accountNumber: c.accountNumber,
          currency: c.currency,
          outstanding: c.outstanding,
          overdue: c.overdue,
          creditLimit: c.creditLimit,
          currencyCreditLimit: c.currencyCreditLimit,
          address: c.address,
          addressLine: c.primaryAddressLine,
          town: c.primaryAddressTown,
          county: c.primaryAddressCounty,
          postcode: c.primaryAddressPostCode,
          country: c.primaryAddressCountry,
          paymentTerms: c.paymentTerms,
          lastPayment: c.lastPayment,
          customerEmail: c.contact || undefined,
          customerPhone: c.phone || undefined,
          isAnyCurrency: c.isAnyCurrency,
          baseCurrency,
          displayOutstanding: c.displayOutstanding,
          displayOverdue: c.displayOverdue,
          outstandingHome: c.outstandingHome,
          overdueHome: c.overdueHome,
        });
      }
    }
    return map;
  }, [selectedCustomerIds, customers, baseCurrency]);

  const buildStatementPdf = useStatementPdfAttachment(statementOptions, customerDataById);

  const fetchStatementAttachments = useCallback(
    async (recipient: { id: string }) => {
      const file = await buildStatementPdf(recipient.id);
      return [file];
    },
    [buildStatementPdf],
  );

  const handleOptionsNext = (options: SendStatementOptions) => {
    setStatementOptions(options);
    onOpenChange(false);
    setEmailDialogOpen(true);
  };

  const handleEmailOpenChange = (nextOpen: boolean) => {
    setEmailDialogOpen(nextOpen);
    if (!nextOpen) setStatementOptions(null);
  };

  const selectedCount = selectedCustomerIds.length;

  return (
    <>
      {/* Step 1 — Statement options */}
      <SendStatementsOptionsModal
        open={open}
        onOpenChange={onOpenChange}
        selectedCustomers={selectedCustomerIds.map((id) => {
          const c = customers.find((x) => x.id === id);
          return {
            id,
            name: c?.name ?? "",
            accountNumber: c?.accountNumber ?? "",
            currency: c?.currency ?? "GBP",
            outstanding: c?.outstanding ?? 0,
            overdue: c?.overdue ?? 0,
            creditLimit: c?.creditLimit ?? 0,
            currencyCreditLimit: c?.currencyCreditLimit,
            address: c?.address ?? "",
            paymentTerms: c?.paymentTerms ?? "",
            lastPayment: c?.lastPayment,
            customerEmail: c?.contact || undefined,
            customerPhone: c?.phone || undefined,
            isAnyCurrency: c?.isAnyCurrency,
            baseCurrency,
            displayOutstanding: c?.displayOutstanding,
            displayOverdue: c?.displayOverdue,
            outstandingHome: c?.outstandingHome,
            overdueHome: c?.overdueHome,
          };
        })}
        onNext={handleOptionsNext}
      />

      {/* Steps 2-4 — Compose, recipients, SendIt MFE */}
      <CustomerSendEmailModal
        open={emailDialogOpen}
        onOpenChange={handleEmailOpenChange}
        isBulkEmail={true}
        stepOverride={{ startAt: 2, total: 4 }}
        allowedTemplateTypes={["email"]}
        bulkCustomers={selectedCustomerIds.map((id) => {
          const c = customers.find((x) => x.id === id);
          return {
            id,
            name: c?.name ?? "",
            accountNumber: c?.accountNumber ?? "",
            contact: c?.contact ?? "",
            phone: c?.phone || undefined,
            outstanding: c?.outstanding,
            overdue: c?.overdue,
            creditLimit: c?.creditLimit,
            currency: c?.currency,
            primaryContactFirstName: c?.primaryContactFirstName,
            primaryContactSurname: c?.primaryContactSurname,
            creditControllerName: c?.creditControllerName,
            creditControllerEmail: c?.creditControllerEmail,
          };
        })}
        fetchAttachments={statementOptions?.fullTemplate?.pdfSettings ? fetchStatementAttachments : undefined}
        attachLabel="Attach statement PDF"
        preparingLabel="Loading Customer Statements"
        onSuccess={() => {
          setEmailDialogOpen(false);
          setStatementOptions(null);
          toast.success(
            `Statements sent to ${selectedCount} ${selectedCount === 1 ? "customer" : "customers"}`,
          );
          onSuccess();
        }}
      />
    </>
  );
}
